<template>
    <div class="g-historical-data">
        <loader class="loader" v-if="loading"></loader>
        <div ref="plotArea" class="plot-area"></div>
    </div>
</template>

<style lang="less" scoped>
/**
 * Helps positioning the loader in the middle with relative position
 */
.g-historical-data {
    position: relative;
}

/**
 * Loader in the middle while loading
 */
.loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

/**
 * That's the height we want for the plot
 */
.plot-area {
    height: 350px;
}
</style>

<style lang="less">
@import (less) '~uplot/dist/uPlot.min.css';

/**
 * Putting the legend on the right
 */
.uplot {
    width: auto;
    display: flex;

    /**
     * Making way for the legend on the right (make sure to change that in JS
     * as well, the size is only computed here so that the flex logic works)
     */
    .u-wrap {
        flex: ~"0 0 calc(100% - 350px)";
    }

    .u-legend {
        display: flex;
        flex: 1 1 100%;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        overflow-y: auto;
        max-height: 350px;
    }
}
</style>

<script>
import moment from 'moment';
import Loader from '../Loader';
import {getHistoricalData} from '../../api/contract';
import UPlot from 'uplot';

/**
 * Converts HSL color into RGB. That's for computing different colors for the
 * lines.
 */
function hslToRgb(h, s, l) {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0;
    let g = 0;
    let b = 0;

    if (h >= 0 && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (h >= 60 && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (h >= 120 && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (h >= 180 && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (h >= 240 && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (h >= 300 && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return `rgb(${r}, ${g}, ${b})`;
}

export default {
    components: {
        Loader
    },

    props: {
        /**
         * Contract object for which we want the graph
         */
        contract: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            /**
             * Indicates that the data is currently being loded
             */
            loading: true,

            /**
             * Data straight out of API
             */
            data: null,

            /**
             * uPlot instance
             */
            uPlot: null,

            /**
             * Plot area size as measured in DOM
             */
            plotAreaSize: null
        };
    },

    methods: {
        /**
         * Check's the DIV size in the DOM directly
         */
        computePlotAreaSize() {
            const {width, height} = this.$el
                .querySelector('.plot-area')
                .getBoundingClientRect();
            this.plotAreaSize = {width, height};
        }
    },

    computed: {
        /**
         * Generates one date for each day of the graph
         */
        plotData() {
            const dates = [];

            const date = moment(this.data.dates.min).set({
                hour: 17,
                minute: 30,
                second: 0,
                millisecond: 0
            });

            const dateMax = moment(this.data.dates.max).set({
                hour: 17,
                minute: 30,
                second: 0,
                millisecond: 0
            });

            while (date <= dateMax) {
                dates.push(date.unix());
                date.add({days: 1});
            }

            return [dates, ...this.data.funds.map(f => f.values)];
        },

        /**
         * Generates the options to display this graph properly
         */
        plotOptions() {
            const {money} = this.$options.filters;

            return {
                width: this.plotAreaSize.width - 350,
                height: 350,
                series: [
                    {},
                    ...this.data.funds.map((f, i) => ({
                        show: f.id === null,
                        label: f.name,
                        value(self, rawValue) {
                            return money(rawValue, 2, false);
                        },
                        stroke: this.plotColors[i]
                    }))
                ]
            };
        },

        /**
         * Generates a unique color for each fund
         */
        plotColors() {
            const out = [];
            const size = (this.data.funds || []).length - 1;

            out.push('black');

            for (let i = 0; i < size; i += 1) {
                out.push(hslToRgb(360 * (i / size), 60, 45));
            }

            return out;
        }
    },

    watch: {
        /**
         * When the data is loaded from the API, generate the uPlot
         */
        data() {
            if (this.uPlot) {
                return;
            }

            this.uPlot = new UPlot(
                this.plotOptions,
                this.plotData,
                this.$el.querySelector('.plot-area')
            );
        }
    },

    /**
     * Starting up shit
     */
    async created() {
        await this.$nextTick(() => {
            this.computePlotAreaSize();
        });
        this.data = await getHistoricalData({
            contractId: this.contract.id
        });
        this.loading = false;
    },

    /**
     * Before the component gets destroyed, clean up the uPlot
     */
    beforeDestroy() {
        this.uPlot.destroy();
        this.uPlot = null;
    }
};
</script>



// WEBPACK FOOTER //
// HistoricalData.vue?eb51a870